import EmberObject, { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { initializePropertyDefaults } from 'sh-www/utils/initialize-state-properties';
import { SH_INVOICE } from "../../../../../models/sh-invoice";

const BATCH_ALL = 'all';
const BATCH_HIDE_CHILDREN = 'hideChildren';
const BATCH_UNBATCHED = 'unbatched';

export default Service.extend({
  BATCH_ALL,
  BATCH_HIDE_CHILDREN,
  BATCH_UNBATCHED,

  /**
   * Register Services
   */
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  propertyDefaults: {
    /**
     * Ids of all the selected invoices for a new batch.
     *
     * @type {Ember.A<String>}
     */
    batchedInvoicesSelected: () => A(),

    /**
     * @type {Boolean}      True if we are creating a batch with the server.
     */
    isCreatingBatch: false,

    /**
     * @type {Boolean}      True if a request to the server is in flight for invoice loading view.
     */
    isLoadingInvoices: false,

    /**
     * @type {Boolean}      True if the select all checkbox has been selected.
     */
    isSelectAllInvoicesForBatchClicked: false,

    /**
     * @type {Boolean}      True when the company sort is going in descending order, ascending otherwise.
     */
    isCompanySortedDescending: true,

    /**
     * This is the current filter object we will use when making our api calls for invoice loading.
     *
     * @return {Object}
     */
    currentFilter: () => EmberObject.create({}),

    /**
     * The current pagination for the invoices we are viewing.
     *
     * @return {Pagination}
     */
    currentPagination: () => EmberObject.create({}),

    /**
     * @type {Number}     Current page we are on in our state.
     */
    page: 1,

    /**
     * All query params for this route's state
     */
    paramApproved: undefined,
    paramAgreement: undefined,
    paramBatch: undefined,
    paramInvoiceId: undefined,
    paramApplicationId: undefined,
    paramCompanyId: undefined,
    paramFinalStatus: undefined,
    paramSentStatus: undefined,
    paramSort: undefined,
    paramUnpaid: undefined,
    paramOverpaid: undefined,
    paramExact: undefined,
    paramPartial: undefined,
    paramStripe: undefined,
    paramInvoice: undefined,
    paramRevisedStatus: undefined,

    /**
     * @type {Ember.A<ShInvoice>}
     */
    shInvoices: () => A(),
  },

  queryParams: computed(
    'paramApproved',
    'paramAgreement',
    'paramBatch',
    'paramInvoiceId',
    'paramApplicationId',
    'paramCompanyId',
    'paramFinalStatus',
    'paramSentStatus',
    'paramSort',
    'paramUnpaid',
    'paramOverpaid',
    'paramExact',
    'paramPartial',
    'paramStripe',
    'paramInvoice',
    'paramRevisedStatus',
    function () {
      return {
        approved : this.get('paramApproved'),
        agreement : this.get('paramAgreement'),
        batch: this.get('paramBatch'),
        id : this.get('paramInvoiceId'),
        applicationId : this.get('paramApplicationId'),
        companyId : this.get('paramCompanyId'),
        final_status : this.get('paramFinalStatus'),
        sent_status : this.get('paramSentStatus'),
        sort : this.get('paramSort'),
        unpaid : this.get('paramUnpaid'),
        overpaid : this.get('paramOverpaid'),
        exact : this.get('paramExact'),
        partial : this.get('paramPartial'),
        stripe : this.get('paramStripe'),
        invoice : this.get('paramInvoice'),
        revised_status : this.get('paramRevisedStatus'),
      };
    }
  ),

  /**
   * @type {Boolean}    True if the invoices we have loaded can be batched.
   */
  canBatch: computed(
  'shInvoices.@each.id',
  'shInvoices.@each.entityId',
  'shInvoices.@each.statusPaidText',
  function() {
    let allInvoices = this.get('shInvoices'),
      areAllGoodStatus = true,
      areAllSameEntity = true,
      chosenEntityId = '';

    allInvoices.forEach(shInvoice => {
      if (!chosenEntityId) {
        chosenEntityId = shInvoice.get('entityId');
      }

      if (chosenEntityId !== shInvoice.get('entityId')) {
        areAllSameEntity = false;
      }

      if (!shInvoice.get('finalAt')) {
        areAllGoodStatus = false;
      }

      if (shInvoice.get('isBatchedInvoice') || shInvoice.get('isInBatchedInvoice')) {
        areAllGoodStatus = false;
      }

      if (shInvoice.get('statusPaidText') === SH_INVOICE.PAID_STATUS_TEXT.PAID_EXACTLY ||
        shInvoice.get('statusPaidText') === SH_INVOICE.PAID_STATUS_TEXT.PARTIALLY_PAID ||
        shInvoice.get('statusPaidText') === SH_INVOICE.PAID_STATUS_TEXT.OVERPAID
      ) {
        areAllGoodStatus = false;
      }
    });

    return areAllSameEntity && areAllGoodStatus;
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    initializePropertyDefaults(this);
  }
});
